import type { OverlappingSourcingPurchaseResponse } from 'api-clients/monolith/models/OverlappingSourcingPurchaseResponse';

type VonqPostingIssues =
  OverlappingSourcingPurchaseResponse['vonq_posting_issues'];
type VonqOpeningIssueEntry = VonqPostingIssues[string];

/**
 * Identifiers for each recognizable field error that can appear in the
 * vonq_posting_issues response. These keys are mapped to human-readable
 * messages in the UI component.
 */
export type VonqFieldErrorKey =
  | 'work_location'
  | 'country'
  | 'street_address'
  | 'city'
  | 'state'
  | 'postal_code'
  | 'job_hours'
  | 'years_of_experience'
  | 'industry'
  | 'job_function'
  | 'company'
  | 'job_title'
  | 'job_description'
  | 'employment_type'
  | 'organization_name'
  | 'company_logo'
  | 'working_location_address'
  | 'working_location_postcode'
  | 'working_location_city'
  | 'working_location_country'
  | 'weekly_working_hours'
  | 'salary_period'
  | 'salary_range'
  | 'job_page_url'
  | 'application_url'
  | 'target_group'
  | 'recruiter_name';

export interface VonqOpeningIssue {
  openingId: string;
  errors: VonqFieldErrorKey[];
}

/**
 * Recursively checks whether a value contains meaningful error content.
 * Handles strings, arrays (including nested arrays), and ignores null/undefined.
 */
function hasError(value: unknown): boolean {
  if (value === null || value === undefined) return false;
  if (typeof value === 'string') return value.length > 0;
  if (Array.isArray(value)) return value.some(item => hasError(item));
  return false;
}

/**
 * Walks through all known fields in an opening's error entry and returns
 * the set of error keys for fields that have errors.
 */
function extractErrorKeys(entry: VonqOpeningIssueEntry): VonqFieldErrorKey[] {
  const errors: VonqFieldErrorKey[] = [];

  // Top-level Fountain validation errors (string values)
  if (entry.work_location) errors.push('work_location');
  if (entry.country) errors.push('country');
  if (entry.street_address) errors.push('street_address');
  if (entry.city) errors.push('city');
  if (entry.state) errors.push('state');
  if (entry.postal_code) errors.push('postal_code');
  if (entry.job_hours) errors.push('job_hours');
  if (entry.years_of_experience) errors.push('years_of_experience');
  if (entry.industry) errors.push('industry');
  if (entry.job_function) errors.push('job_function');

  if (hasError(entry.companyId)) errors.push('company');

  // Nested posting details — each leaf is an Array<string> of error messages
  if (entry.postingDetails) {
    const pd = entry.postingDetails;
    if (hasError(pd.title)) errors.push('job_title');
    if (hasError(pd.description)) errors.push('job_description');
    if (hasError(pd.yearsOfExperience)) errors.push('years_of_experience');
    if (hasError(pd.employmentType)) errors.push('employment_type');
    if (hasError(pd.organization?.name)) errors.push('organization_name');
    if (hasError(pd.organization?.companyLogo)) errors.push('company_logo');
    if (hasError(pd.workingLocation?.addressLine1))
      errors.push('working_location_address');
    if (hasError(pd.workingLocation?.postcode))
      errors.push('working_location_postcode');
    if (hasError(pd.workingLocation?.city))
      errors.push('working_location_city');
    if (hasError(pd.workingLocation?.country))
      errors.push('working_location_country');
    if (hasError(pd.weeklyWorkingHours?.to))
      errors.push('weekly_working_hours');
    if (hasError(pd.salaryIndication?.period)) errors.push('salary_period');
    if (hasError(pd.salaryIndication?.range?.to)) errors.push('salary_range');
    if (hasError(pd.jobPageUrl)) errors.push('job_page_url');
    if (hasError(pd.applicationUrl)) errors.push('application_url');
  }

  if (hasError(entry.recruiterInfo?.name)) errors.push('recruiter_name');

  // Deduplicate (e.g. years_of_experience can appear at top level and in postingDetails)
  return Array.from(new Set(errors));
}

/**
 * Parses the `vonq_posting_issues` response and returns a structured list of
 * issues grouped by opening. Only openings with at least one detected error
 * are included in the result.
 *
 * @param issues - The raw vonq_posting_issues record from the API response
 */
export function parseVonqPostingIssues(
  issues: VonqPostingIssues | undefined | null,
): VonqOpeningIssue[] {
  if (!issues) return [];

  const result: VonqOpeningIssue[] = [];

  for (const [openingId, entry] of Object.entries(issues)) {
    const errors = extractErrorKeys(entry);
    if (errors.length === 0) continue;

    result.push({ openingId, errors });
  }

  return result;
}
